import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Switch } from 'utoniq-ui'

import {
  ArtistUpdatePreSaleInput,
  ConsolePreSaleEditFragment,
  Maybe,
  MembershipInterval,
  MembershipSubscriptionPlan,
} from '@/clients/utoniq-core/schema'
import {
  DatePickerCell,
  PaymentMethodsInputCell,
  TextInputCell,
  ToggleInputCell,
} from '@/components/pages/ConsolePreSaleCreate/template/components'
import { getPaymentMethods } from '@/components/pages/ConsolePreSaleEdit/template/LotteryEditForm'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { Form, tLabel, useForm, UseFormProps } from '@/core/form'

export type FirstComeFirstServedEditValues = Omit<
  ArtistUpdatePreSaleInput,
  'paymentMethods'
> & {
  purchasableCount: number
  paymentMethods: string
  purchasableStartedAt: string
  purchasableEndedAt: string
  purchesablePlanIds: string[]
}

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(30)
    .label(tLabel('consolePreSaleCreate.title'))
    .required(),
  description: o
    .string()
    .max(5000)
    .label(tLabel('consolePreSaleCreate.description'))
    .required(),
  note: o
    .string()
    .max(5000)
    .label(tLabel('consolePreSaleCreate.note'))
    .required(),
  purchasableCount: o
    .number()
    .label(tLabel('consolePreSaleCreate.purchasableCount'))
    .max(8)
    .optional(),
  paymentMethods: o
    .string()
    .optional()
    .label('consolePreSaleCreate.paymentMethods'),
  purchasableStartedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.purchasableStartedAt'))
    .required(),
  purchasableEndedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.purchasableEndedAt'))
    .required(),
  isPublished: o.bool().label(tLabel('consolePreSaleCreate.isPublished')),
  startedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.startedAt'))
    .required(),
  endedAt: o.date().label(tLabel('consolePreSaleCreate.endedAt')).required(),
  purchesablePlanIds: o
    .array()
    .label(tLabel('consolePreSaleCreate.membershipAccessPermission'))
    .items(o.string())
    .optional()
    .allow(null),
  isPremium: o.bool().label(tLabel('consolePreSaleCreate.isPremium')),
})

export const FirstComeFirstServedEditForm: Organism<{
  submitting: boolean
  preSale: ConsolePreSaleEditFragment
  onSubmit: (values: FirstComeFirstServedEditValues) => Promise<void>
  plans: Maybe<
    Pick<MembershipSubscriptionPlan, 'planId' | 'title' | 'interval'>
  >[]
}> = ({ preSale, onSubmit, submitting, plans }) => {
  const { t } = useTranslation('consolePreSaleEdit')

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    reset,
    watch,
  } = useForm<FirstComeFirstServedEditValues>({
    schema,
    defaultValues,
  })

  const isPremium = watch('isPremium')
  const purchesablePlanIds = watch('purchesablePlanIds')
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    reset({
      title: preSale.title,
      description: preSale.description,
      note: preSale.note,
      paymentMethods: getPaymentMethods(preSale.paymentMethods),
      isPublished: preSale.isPublished,
      startedAt: preSale.startedAt,
      endedAt: preSale.endedAt,
      purchasableCount: preSale.firstComeFirstServed?.purchasableCount,
      purchasableStartedAt: preSale.firstComeFirstServed?.purchasableStartedAt,
      purchasableEndedAt: preSale.firstComeFirstServed?.purchasableEndedAt,
      purchesablePlanIds:
        preSale.purchesablePlanIds?.filter((id): id is string => id !== null) ??
        [],
      isPremium: preSale.isPremium,
    })
  }, [preSale])

  useEffect(() => {
    if (isPremium && (!purchesablePlanIds || purchesablePlanIds.length === 0)) {
      setShowError(true)
    } else {
      setShowError(false)
    }
  }, [isPremium, purchesablePlanIds])

  const isFormValid = isValid && !showError

  return (
    <Form onSubmit={handleSubmit(onSubmit)} sx={{ pt: 6, pb: 9 }}>
      <TextInputCell type="text" error={errors.title} {...register('title')} />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors.description}
        {...register('description')}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors.note}
        {...register('note')}
      />
      <TextInputCell
        type="tel"
        error={errors.purchasableCount}
        {...register('purchasableCount')}
        disabled
      />
      {/* 応募可能なユーザー */}
      {plans && (
        <>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Stack flex={1}>
              <Typography variant="h6">
                {t('membershipAccessPermission')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('membershipAccessPermissionDesc')}
              </Typography>
            </Stack>
            <Stack>
              <Switch
                sx={{
                  '&& .Mui-disabled + .MuiSwitch-track': {
                    bgcolor: 'grey.100',
                  },
                  '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                    bgcolor: 'primary.main',
                  },
                }}
                checked={watch('isPremium') ?? false}
                onChange={() => {
                  if (watch('isPremium')) {
                    setValue('purchesablePlanIds', [] as never, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                    setValue('isPremium', false, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  } else {
                    setValue(
                      'purchesablePlanIds',
                      preSale.purchesablePlanIds?.filter(
                        (id): id is string => id !== null
                      ) ?? [],
                      { shouldValidate: true, shouldDirty: true }
                    )
                    setValue('isPremium', !watch('isPremium'), {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                }}
              />
            </Stack>
          </Stack>
          {watch('isPremium') && plans.length > 1 && (
            <FormControl error={showError} sx={{ mt: 2 }}>
              {plans.map((plan, index) => (
                <Box key={index}>
                  <FormControlLabel
                    key={plan?.planId}
                    label={
                      plan?.interval === MembershipInterval.Year
                        ? t('planOnlyYear', { name: plan?.title ?? '' })
                        : plan?.title ?? ''
                    }
                    {...register('purchesablePlanIds')}
                    control={
                      <Checkbox
                        onChange={e => {
                          const currentPlanIds = (
                            watch('purchesablePlanIds') || []
                          ).filter((id): id is string => id !== null)
                          if (e.target.checked) {
                            // チェックされた場合、planIdを追加
                            setValue('purchesablePlanIds', [
                              ...currentPlanIds,
                              plan?.planId ?? '', // planIdがundefinedの場合は空文字を使用
                            ])
                          } else {
                            // チェックが解除された場合、planIdを削除
                            setValue(
                              'purchesablePlanIds',
                              currentPlanIds.filter(id => id !== plan?.planId)
                            )
                          }
                        }}
                        checked={
                          watch('purchesablePlanIds')?.includes(
                            plan?.planId ?? ''
                          ) || false
                        }
                      />
                    }
                  />
                </Box>
              ))}
              <Typography variant="caption" color="error.main">
                {showError ? t('membershipAccessPermissionRequired') : ' '}
              </Typography>
            </FormControl>
          )}
        </>
      )}

      {/* 決済方法設定 */}
      <Box sx={{ mt: 8 }}>
        <PaymentMethodsInputCell
          paymentMethods={watch('paymentMethods') as unknown as string}
          setValue={setValue}
          disabled
        />
      </Box>
      {/* 決済期間 */}
      <Typography variant="h5" sx={{ mb: 4 }}>
        {t('purchasablePeriodFirstComeFirstServed')}
      </Typography>
      <DatePickerCell
        name="purchasableStartedAt"
        desc={t('purchasableStartedAtDesc')}
        error={errors.purchasableStartedAt}
        setValue={setValue}
        value={watch('purchasableStartedAt')}
      />
      <DatePickerCell
        name="purchasableEndedAt"
        error={errors.purchasableEndedAt}
        setValue={setValue}
        value={watch('purchasableEndedAt')}
      />
      {/* 公開設定 */}
      <ToggleInputCell
        content={{
          title: t('isPublishedTitle'),
          description: t('isPublishedDesc'),
        }}
        {...register('isPublished')}
        checked={watch('isPublished')}
      />
      <DatePickerCell
        name="startedAt"
        error={errors.startedAt}
        setValue={setValue}
        value={watch('startedAt')}
      />
      <DatePickerCell
        name="endedAt"
        error={errors.endedAt}
        setValue={setValue}
        value={watch('endedAt')}
      />

      {/* 送信ボタン */}
      <Stack sx={{ mt: 5, gap: 5 }} alignItems="center">
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={submitting}
          disabled={!isFormValid || submitting}
        >
          {t('submit')}
        </Button>
      </Stack>
    </Form>
  )
}

const defaultValues = {
  paymentMethods: 'both',
}
